import React, { useEffect, useState, useRef } from "react";
import { Spinner } from "reactstrap";
import { Tooltip } from "antd";

const getTruckImage = (trucktype) => {
  try {
    return `https://media.qiktruck.co.za/3d-${trucktype}.png`;
  } catch (error) {
    return "";
  }
};

const rankColor = (idx) =>
  idx === 0 ? "#ffd700" : idx === 1 ? "#c0c0c0" : "#cd7f32";

const tripCountLabel = (n) => {
  const count = Number(n) || 0;
  return `${count} trip${count === 1 ? "" : "s"}`;
};

// Confetti particle colors (brand-friendly)
const CONFETTI_COLORS = [
  "#f5861f",
  "#ffd700",
  "#ff4d6d",
  "#38b000",
  "#3a86ff",
  "#9d4edd",
];

// 18 static pre-computed positions so particles fan out nicely
const CONFETTI_PARTICLES = Array.from({ length: 18 }).map((_, i) => {
  const angle = (i / 18) * Math.PI * 2;
  const distance = 40 + ((i * 7) % 30);
  return {
    dx: Math.cos(angle) * distance,
    dy: Math.sin(angle) * distance - 10,
    rot: (i * 40) % 360,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: (i % 6) * 20,
  };
});

/**
 * Compact hero panel showing the customer's top active drivers.
 * Clicking a driver card triggers onDriverSelect(driver).
 */
const TopActiveDriversPanel = ({
  drivers = [],
  loading = false,
  onDriverSelect,
  onRefresh,
  onTakeTour,
}) => {
  const [celebrate, setCelebrate] = useState(false);
  const hasCelebratedRef = useRef(false);

  useEffect(() => {
    if (
      drivers.length > 0 &&
      !loading &&
      !hasCelebratedRef.current
    ) {
      hasCelebratedRef.current = true;
      // small delay so the user sees the card mount before it shakes
      const t = setTimeout(() => setCelebrate(true), 250);
      const t2 = setTimeout(() => setCelebrate(false), 2000);
      return () => {
        clearTimeout(t);
        clearTimeout(t2);
      };
    }
  }, [drivers, loading]);

  return (
    <div
      style={{
        background: "#fff7ef",
        border: "1px solid #ffd9b3",
        borderRadius: 8,
        padding: "8px 10px",
        marginBottom: 10,
      }}
    >
      {/* Local animations */}
      <style>{`
        @keyframes qt-top-driver-shake {
          0%   { transform: translateX(0) rotate(0); }
          15%  { transform: translateX(-4px) rotate(-2deg); }
          30%  { transform: translateX(4px) rotate(2deg); }
          45%  { transform: translateX(-3px) rotate(-1.5deg); }
          60%  { transform: translateX(3px) rotate(1.5deg); }
          75%  { transform: translateX(-2px) rotate(-1deg); }
          90%  { transform: translateX(2px) rotate(1deg); }
          100% { transform: translateX(0) rotate(0); }
        }
        .qt-top-driver-shake {
          animation: qt-top-driver-shake 0.8s ease-in-out 1;
          box-shadow: 0 0 0 2px #ffd700, 0 4px 14px rgba(245, 134, 31, 0.35) !important;
        }
        @keyframes qt-confetti-pop {
          0%   { opacity: 0; transform: translate(0,0) rotate(0) scale(0.6); }
          15%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--qt-dx), var(--qt-dy)) rotate(var(--qt-rot)) scale(1); }
        }
        .qt-confetti-piece {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 6px;
          height: 10px;
          border-radius: 1px;
          pointer-events: none;
          animation: qt-confetti-pop 1.4s ease-out forwards;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <Tooltip
          title="Your most active drivers in the last 60 days. Tap any driver to see their last 10 trips and instantly rebook with the same route, goods and pricing."
          placement="bottomLeft"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 700,
              color: "#222",
              cursor: "help",
            }}
          >
            <i
              className="mdi mdi-account-star"
              style={{ color: "#f5861f", fontSize: 16 }}
            ></i>
            Top Active Drivers
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#888",
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              last 60 days
            </span>
            <i
              className="mdi mdi-information-outline"
              style={{ color: "#f5861f", fontSize: 13 }}
            ></i>
          </div>
        </Tooltip>
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Refresh driver list">
            <button
              className="flat-button"
              style={{ fontSize: 11, padding: "2px 8px" }}
              onClick={onRefresh}
              disabled={loading}
            >
              <i className="mdi mdi-refresh"></i>
            </button>
          </Tooltip>
          {onTakeTour && (
            <Tooltip title="Replay the dashboard walkthrough">
              <button
                className="flat-button"
                style={{ fontSize: 11, padding: "2px 8px" }}
                onClick={onTakeTour}
              >
                <i className="mdi mdi-compass-outline"></i> Tour
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ padding: 8, textAlign: "center" }}>
          <Spinner size="sm" />
        </div>
      )}

      {!loading && drivers.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 8,
            color: "#999",
            fontSize: 12,
          }}
        >
          No active drivers yet
        </div>
      )}

      {drivers.length > 0 && (
        <div
          className="d-flex flex-row"
          style={{
            gap: 6,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {drivers.map((d, idx) => {
            const isTop = idx === 0;
            const showCelebration = isTop && celebrate;
            return (
            <Tooltip
              key={d.vehicleid}
              title={`${tripCountLabel(d.trips)} completed with ${d.firstname}. Click to view their recent trips and rebook.`}
              placement="bottom"
            >
              <div
                className={
                  "flex-shrink-0" + (showCelebration ? " qt-top-driver-shake" : "")
                }
                style={{
                  minWidth: 150,
                  width: 150,
                  padding: 6,
                  cursor: "pointer",
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 6,
                  position: "relative",
                }}
                onClick={() => onDriverSelect && onDriverSelect(d)}
              >
                {showCelebration && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      overflow: "visible",
                      pointerEvents: "none",
                    }}
                  >
                    {CONFETTI_PARTICLES.map((p, i) => (
                      <span
                        key={i}
                        className="qt-confetti-piece"
                        style={{
                          backgroundColor: p.color,
                          animationDelay: `${p.delay}ms`,
                          "--qt-dx": `${p.dx}px`,
                          "--qt-dy": `${p.dy}px`,
                          "--qt-rot": `${p.rot}deg`,
                        }}
                      />
                    ))}
                  </div>
                )}
                {idx < 3 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: rankColor(idx),
                      color: "#fff",
                      borderRadius: "50%",
                      width: 16,
                      height: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 9,
                      fontWeight: "bold",
                    }}
                  >
                    {idx + 1}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <img src={getTruckImage(d.trucktype)} alt="" height="22" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: "#222",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {d.firstname} {d.lastname}
                    </div>
                    <div style={{ fontSize: 10, color: "#888" }}>
                      {tripCountLabel(d.trips)} (60d) · {d.trucktype}
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopActiveDriversPanel;
